import Box from '@mui/material/Box';
import { type JSX, useEffect, useState } from 'react';

import { ClientTPRMApi } from 'Api/TPRM/TPRMApi';
import { ClientUcrmApi } from 'Api/UCRM/UcrmApi';
import { Alert } from 'Components/Alert/Alert';
import { Button } from 'Components/Buttons/Buttons';
import { ChangeEventType, FormFieldSelect } from 'Components/FormField/FormFieldSelect/FormFieldSelect';
import { Modal } from 'Components/Modal/Modal';
import { Text } from 'Components/Text/Text';
import { ICON_CLOSE } from 'Config/Icons';
import { AssessmentSubjectBase, AssessmentSubjectSummary, UpdateCommonAssessmentRequest } from 'Models/AssessmentSubject';
import { ValidationError } from 'Models/ErrorTypes';
import { DueDiligenceModule, ProductModule } from 'Models/ProductModule';

import styles from './CommonAssessmentSelectionModal.module.css';

export interface CommonAssessmentSelectionModalProps {
    assessmentSubject: AssessmentSubjectBase;
    hideModal: () => void;
    module: DueDiligenceModule;
    moduleApi: ClientTPRMApi | ClientUcrmApi;
    onComplete: () => void;
}

interface FormState {
    isLoading: boolean;
    isComplete: boolean;
    successMessage?: string;
    failureMessage?: string;
}

/** Lets the user choose another assessment subject (with the same organization, and not itself using shared assessments) to provide shared due diligence assessments for `assessmentSubject`. Used on the TPRM Service Dashboard and the UCRM Use Case Dashboard. */
export const CommonAssessmentSelectionModal = (props: CommonAssessmentSelectionModalProps): JSX.Element => {
    const [selectableAssessmentSubjects, setSelectableAssessmentSubjects] = useState<AssessmentSubjectSummary[] | undefined>(undefined);
    const [selectedAssessmentSubject, setSelectedAssessmentSubject] = useState<AssessmentSubjectSummary>();
    const [formState, setFormState] = useState<FormState>({ isLoading: false, isComplete: false });

    useEffect(() => {
        const getSelectableAssessmentSubjects = async (): Promise<void> => {
            try {
                const assessmentSubjectSummaries = (await props.moduleApi.getCommonAssessmentPotentialParents(props.assessmentSubject.vendor_id, props.assessmentSubject.id)).data;
                setSelectableAssessmentSubjects(
                    assessmentSubjectSummaries.sort((a, b) => {
                        return a.name.localeCompare(b.name);
                    })
                );
            } catch (error) {
                setFormState({ isLoading: false, isComplete: false, failureMessage: error.message });
            }
        };

        getSelectableAssessmentSubjects();
    }, [props.assessmentSubject, props.moduleApi]);

    const handleSelectChange = (value: ChangeEventType, formFieldId: string): void => {
        if (value === undefined || value === null) {
            setSelectedAssessmentSubject(undefined);
        } else {
            const selectedAssessmentSubject = selectableAssessmentSubjects!.find((summary) => summary.id === value)!;
            setSelectedAssessmentSubject(selectedAssessmentSubject);
        }
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault();

        try {
            validateForm();
            setFormState({ isLoading: true, isComplete: false });

            const request: UpdateCommonAssessmentRequest = {
                parent_identifier: { organization_id: selectedAssessmentSubject!.vendor_id, assessment_subject_id: selectedAssessmentSubject!.id },
                child_identifier: { organization_id: props.assessmentSubject.vendor_id, assessment_subject_id: props.assessmentSubject.id },
                linked: true,
            };
            await props.moduleApi.updateCommonAssessment(request);

            setFormState({ isLoading: false, isComplete: true, successMessage: 'Due diligence assessment sharing updated.' });
            props.onComplete();
        } catch (error) {
            setFormState({ isLoading: false, isComplete: false, failureMessage: error.message });
        }
    };

    const assessmentSubjectOptions = selectableAssessmentSubjects?.map((summary) => {
        return {
            value: summary.id,
            label: summary.name,
        };
    });

    const selectionPossible = selectableAssessmentSubjects && selectableAssessmentSubjects.length > 0;

    const validateForm = () => {
        if (!selectedAssessmentSubject) {
            throw new ValidationError(`You must select a ${props.module === ProductModule.UCRM ? 'use case' : 'third-party service'}.`);
        }
    };

    if (!assessmentSubjectOptions) {
        return <Modal open isLoading closeModal={props.hideModal} header="Shared Due Diligence Assessments" failureMessage={formState.failureMessage} />;
    }

    return (
        <Modal open header="Shared Due Diligence Assessments">
            {formState.successMessage && <Alert type="success">{formState.successMessage}</Alert>}
            {formState.failureMessage && <Alert type="error">{formState.failureMessage}</Alert>}
            <Box component="form" noValidate onSubmit={handleSubmit}>
                <Text>{props.module === ProductModule.UCRM ? 'Use cases sharing due diligence assessments will share the same:' : 'Third-party services sharing due diligence assessments will share the same:'}</Text>
                <ul>
                    <Text>
                        <li>Due diligence questionnaires</li>
                        <li>Due diligence assessments</li>
                        <li>Overall control effectiveness</li>
                    </Text>
                </ul>
                {selectionPossible && (
                    <>
                        <Text>{props.module === ProductModule.UCRM ? `Select the use case that will provide shared due diligence assessments for ${props.assessmentSubject.name}.` : `Select the third-party service that will provide shared due diligence assessments for ${props.assessmentSubject.name}.`}</Text>
                        <div className={styles.serviceSelect}>
                            <FormFieldSelect formFieldId="commonAssessmentParent" formFieldLabel={props.module === ProductModule.UCRM ? 'Use Case' : 'Third-Party Service'} options={assessmentSubjectOptions} handleChange={handleSelectChange} />
                        </div>
                    </>
                )}
                {!selectionPossible && <Text>{props.module === ProductModule.UCRM ? `There are no use cases available to provide shared due diligence assessments for ${props.assessmentSubject.name}.` : `There are no third-party services available to provide shared due diligence assessments for ${props.assessmentSubject.name}.`}</Text>}
                <div className={'modalFormButtonContainer'}>
                    <Button variant="secondary" onClick={props.hideModal} fontAwesomeImage={ICON_CLOSE}>
                        Close
                    </Button>
                    {selectionPossible && (
                        <Button variant="submit" disabled={formState.isComplete} isLoading={formState.isLoading} loadingText="Saving...">
                            Save
                        </Button>
                    )}
                </div>
            </Box>
        </Modal>
    );
};
